import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '@@/contexts/AuthContext';
import { InsightsApiClient, SearchContactsRequest, SearchContactsResult } from '../../api/api-client';
import { ResultValue } from '@@/types/ApiResult';

export const useSearchContacts = (filters: SearchContactsRequest) => {
  const { instanceData } = useContext(AuthContext);

  const fetchContacts = async (): Promise<SearchContactsResult> => {
    if (!instanceData?.instanceId) {
      throw new Error('Missing required context data');
    }

    const client = new InsightsApiClient();
    const result = await client.searchContacts({
      instanceId: instanceData.instanceId,
      region: instanceData.region,
      contactId: filters.contactId,
      agentIds: filters.agentIds,
      initiationMethods: filters.initiationMethods,
      queueIds: filters.queueIds,
      channels: filters.channels,
      includePending: filters.includePending,
      timeRange: filters.timeRange,
      segmentAttributes: filters.segmentAttributes,
      contactAttributes: filters.contactAttributes
    });

    if (!ResultValue.ok(result)) {
      throw new Error('Failed to fetch contacts');
    }

    return result.data;
  };

  const { data, isLoading, error, refetch, isRefetching } = useQuery<SearchContactsResult, Error>({
    queryKey: ['contacts', instanceData?.instanceId, instanceData?.region, filters],
    queryFn: fetchContacts,
    retry: 0
  });

  return {
    data: data,
    isLoading: isLoading,
    isRefetching: isRefetching,
    error,
    refetch
  };
};
